import type { KeyboardEvent, MouseEvent } from 'react';
import classNames from 'classnames';

import NextImage from '@com/_core/NextImage';
import { Button } from '@com/_atoms/NewButton';
import Icon from '@utilities/icon';
import { colors } from '@configs/Theme';
import { colorPallete } from '@configs/color-pallete';
import { verticalDoctorCardTexts } from '@constant/home';
import { DoctorActivityStatusEnum } from '@models/ocs/doctor';

const ICON_SIZE = 0.75;
const IMG_SIZE = 56;

export type VerticalDoctorCardProps = {
  activity?: DoctorActivityStatusEnum;
  isScheduled?: boolean;
  onClick?: () => void;
  isOndemand?: boolean;
  isLoading?: boolean;
  buttonText?: string;
  isActive?: boolean;
  reserveCitiesRow?: boolean;
  className?: string;
  cities?: string[];
  imageUrl: string;
  subtitle: string;
  title: string;
  star: number;
};

const VerticalDoctorCard = ({
  isActive: isActiveProp,
  activity,
  cities,
  isOndemand,
  isScheduled,
  reserveCitiesRow,
  className,
  imageUrl,
  star,
  title,
  subtitle,
  onClick,
  isLoading,
  buttonText,
}: VerticalDoctorCardProps) => {
  const showStatusIndicator =
    activity !== undefined || isActiveProp !== undefined;

  const resolvedIsActive =
    isActiveProp ??
    (activity !== undefined &&
      activity !== DoctorActivityStatusEnum.Offline);

  const resolvedButtonText =
    buttonText ??
    (resolvedIsActive
      ? verticalDoctorCardTexts.viewOnlineProfile
      : verticalDoctorCardTexts.viewOfflineProfile);

  const citiesLabel = cities?.length ? cities.join(' - ') : null;
  const showCitiesRow = reserveCitiesRow || !!citiesLabel;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      onClick?.();
    }
  };

  const onButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClick?.();
  };

  return (
    <div
      className={classNames(
        'w-[152px] h-[178px] shrink-0 flex flex-col rounded-lg bg-surface-primary overflow-hidden shadow-fab',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="relative shrink-0 h-[78px] px-3 pt-2">
        <div
          className={classNames(
            'h-14 absolute inset-x-0 top-0 rounded-t-lg',
            resolvedIsActive
              ? 'bg-gradient-to-b from-green-100 to-surface-primary'
              : 'bg-gradient-to-b from-surface-tertiary to-surface-primary',
          )}
        />

        <div className="relative flex items-start justify-between gap-2">
          <div className="flex flex-col items-center z-10 -mb-1">
            <div className="relative">
              <div
                className={classNames(
                  'w-14 h-14 flex items-center justify-center rounded-full overflow-hidden border-2',
                  resolvedIsActive
                    ? 'border-green-300'
                    : 'border-surface-primary',
                )}
              >
                <NextImage
                  src={imageUrl}
                  alt="doctor"
                  width={IMG_SIZE}
                  height={IMG_SIZE}
                />
              </div>
              {showStatusIndicator && (
                <div className="absolute top-1 right-0 flex items-center justify-center rounded-full bg-surface-primary p-[2px]">
                  {resolvedIsActive ? (
                    <span className="w-2 h-2 relative flex">
                      <span className="w-full h-full absolute inline-flex rounded-full bg-green-400 opacity-75 animate-ping" />
                      <span className="w-2 h-2 relative inline-flex rounded-full bg-green-300" />
                    </span>
                  ) : (
                    <span className="w-2 h-2 inline-flex rounded-full bg-surface-tertiary border border-border-primary/20" />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-1">
            {isScheduled && (
              <div className="w-max h-5 shadow-fab flex items-center gap-x-0.5 rounded-full bg-surface-primary px-2">
                <Icon
                  name="Calendar"
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  fill={colors.blue[400]}
                />
                <span className="text-Label/2xSmall text-content-secondary">
                  زمانبندی
                </span>
              </div>
            )}
            {resolvedIsActive && isOndemand && (
              <div className="w-max h-5 shadow-fab flex items-center gap-x-0.5 rounded-full bg-surface-primary px-2">
                <Icon
                  name="LightningBolt"
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  fill={colors.blue[400]}
                />
                <span className="text-Label/2xSmall text-content-secondary">
                  فوری
                </span>
              </div>
            )}
            <div className="w-max h-5 shadow-fab flex items-center gap-x-0.5 rounded-full bg-surface-primary px-2">
              <Icon
                name="StarFill"
                width={ICON_SIZE}
                height={ICON_SIZE}
                fill={colors.yellow[500]}
              />
              <span className="text-Label/2xSmall text-content-secondary">
                {star}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center px-3 pt-1">
        <h3 className="w-full h-5 shrink-0 text-center text-Label/Small text-content-primary truncate">
          {title}
        </h3>
        <p className="w-full h-4 my-1 shrink-0 text-center text-Body/xSmall text-content-tertiary truncate">
          {subtitle}
        </p>

        <div
          className={classNames(
            'flex h-4 w-full shrink-0 items-center justify-center gap-1 text-Label/2xSmall text-content-tertiary',
            showCitiesRow && !citiesLabel && 'invisible',
            !showCitiesRow && 'hidden',
          )}
          aria-hidden={!citiesLabel}
        >
          <Icon
            name="PinCircle"
            width={0.875}
            height={0.875}
            fill={colorPallete?.content?.tertiary}
          />
          <span className="min-w-0 truncate">{citiesLabel ?? '\u00a0'}</span>
        </div>

        <div className="mt-auto w-full shrink-0">
          <Button
            variant="primary"
            size="small"
            className={classNames(
              'w-full mb-3 !rounded-full !text-Label/Small',
              resolvedIsActive
                ? '!bg-green-400 !border-green-400'
                : '!bg-content-primary !border-content-primary',
            )}
            onClick={onButtonClick}
            isLoading={isLoading}
          >
            {resolvedButtonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VerticalDoctorCard;
